import { Link } from '@tanstack/react-router';
import {
  EmptyState,
  SparklineMini,
  StatusPill,
  TextButton,
  type StatusLevel,
} from '@/lib';
import type { ApiClients } from '@/api/client';
import { orgRoute } from '@/app/org-route';
import { useSensorDiagnostics, useSensorThresholds } from './queries';

export type S6SensorDetailProps = {
  clients: ApiClients;
  sensorId: string;
};

function numericPointY(p: [number, number] | { x: number; y: number }): number {
  return Array.isArray(p) ? p[1] : p.y;
}

const STATUS_TONE: Record<string, StatusLevel> = {
  healthy: 'healthy',
  ok: 'healthy',
  watch: 'watch',
  degraded: 'warn',
  warn: 'warn',
  critical: 'critical',
  offline: 'critical',
};

/**
 * S6 Sensor Detail. Composes /v1/sensors/{id} + /v1/sensors/{id}/thresholds:
 *   - header: sensor name + asset/position + status pill + Analyze button
 *   - diagnostics column: battery + signal trends as sparklines, firmware
 *   - quality warnings + heartbeat history
 *   - thresholds table
 *
 * The "Analyze ↗" link routes to /analyze with the sensor pre-selected so
 * the operator can dive into the chart workspace.
 */
export function S6SensorDetail({ clients, sensorId }: S6SensorDetailProps) {
  const diagnostics = useSensorDiagnostics(clients, sensorId);
  const thresholds = useSensorThresholds(clients, sensorId);

  if (diagnostics.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load sensor"
        body={String(diagnostics.error)}
        cta={{ label: 'Retry', onClick: () => diagnostics.refetch() }}
      />
    );
  }
  if (diagnostics.isLoading || !diagnostics.data) {
    return <p className="text-fs-body text-gray-700">Loading sensor…</p>;
  }

  const d = diagnostics.data;
  const tone = STATUS_TONE[d.summary.status.toLowerCase()] ?? 'watch';
  const batterySeries = d.batteryTrend.map(numericPointY);
  const signalSeries = d.signalTrend.map(numericPointY);

  return (
    <div className="flex flex-col gap-sp-6">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Sensor · {d.summary.type}
          </p>
          <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">
            {d.summary.name}
          </h1>
          <p className="mt-sp-1 text-fs-meta text-gray-700">
            {d.summary.assetName} · {d.summary.positionName} · firmware{' '}
            <code className="font-mono">{d.firmwareVersion}</code>
          </p>
        </div>
        <div className="flex items-center gap-sp-3">
          <StatusPill level={tone} label={d.summary.status.toUpperCase()} />
          <Link
            to={orgRoute('/analyze')}
            search={{ sensors: [d.summary.sensorId] }}
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-1 text-fs-meta text-gray-900 hover:border-gray-500"
          >
            Analyze ↗
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-sp-6 lg:grid-cols-2">
        <section
          aria-labelledby="diagnostics-heading"
          className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
        >
          <h2
            id="diagnostics-heading"
            className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
          >
            Diagnostics
          </h2>
          <dl className="mt-sp-3 grid grid-cols-[8rem_1fr] gap-y-sp-2 text-fs-body text-gray-900">
            <dt className="text-gray-700">Battery</dt>
            <dd className="flex items-center gap-sp-3">
              <span className="font-mono">{d.summary.batteryPct.toFixed(0)}%</span>
              {batterySeries.length > 0 ? (
                <SparklineMini series={batterySeries} tone="neutral" />
              ) : null}
            </dd>
            <dt className="text-gray-700">Signal</dt>
            <dd className="flex items-center gap-sp-3">
              <span className="font-mono">
                {d.summary.signalStrengthDbm.toFixed(0)} dBm
              </span>
              {signalSeries.length > 0 ? (
                <SparklineMini series={signalSeries} tone="neutral" />
              ) : null}
            </dd>
            <dt className="text-gray-700">Heartbeats</dt>
            <dd className="font-mono text-fs-meta text-gray-700">
              {d.heartbeatHistory.length > 0
                ? `${d.heartbeatHistory.length} recent`
                : 'none yet'}
            </dd>
          </dl>
        </section>

        <section
          aria-labelledby="warnings-heading"
          className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
        >
          <h2
            id="warnings-heading"
            className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
          >
            Quality warnings
          </h2>
          {d.qualityWarnings.length === 0 ? (
            <p className="mt-sp-3 text-fs-body text-gray-700">No warnings.</p>
          ) : (
            <ul className="mt-sp-3 flex flex-col gap-sp-2">
              {d.qualityWarnings.map((w, i) => (
                <li
                  key={i}
                  className="flex items-start gap-sp-2 text-fs-body text-gray-900"
                >
                  <span
                    aria-hidden
                    className="mt-sp-1 inline-block h-sp-2 w-sp-2 rounded-r-chip bg-warn"
                  />
                  {w}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section aria-labelledby="thresholds-heading">
        <div className="flex items-baseline justify-between">
          <h2
            id="thresholds-heading"
            className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
          >
            Thresholds
          </h2>
          {thresholds.isError ? (
            <TextButton label="Retry" onClick={() => thresholds.refetch()} />
          ) : null}
        </div>
        {thresholds.isLoading ? (
          <p className="mt-sp-3 text-fs-body text-gray-700">Loading thresholds…</p>
        ) : !thresholds.data || thresholds.data.length === 0 ? (
          <p className="mt-sp-3 text-fs-body text-gray-700">
            No thresholds configured for this sensor.
          </p>
        ) : (
          <div className="mt-sp-3 overflow-x-auto rounded-r-card border border-gray-300 bg-gray-50">
            <table className="min-w-full text-fs-meta">
              <thead>
                <tr className="text-gray-700">
                  <th className="px-sp-3 py-sp-2 text-left font-medium uppercase tracking-[0.14em]">
                    Metric
                  </th>
                  <th className="px-sp-3 py-sp-2 text-right font-medium uppercase tracking-[0.14em]">
                    Warn
                  </th>
                  <th className="px-sp-3 py-sp-2 text-right font-medium uppercase tracking-[0.14em]">
                    Critical
                  </th>
                  <th className="px-sp-3 py-sp-2 text-left font-medium uppercase tracking-[0.14em]">
                    Unit
                  </th>
                  <th className="px-sp-3 py-sp-2 text-left font-medium uppercase tracking-[0.14em]">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {thresholds.data.map((t) => (
                  <tr key={t.metricKey} className="border-t border-gray-300">
                    <td className="px-sp-3 py-sp-2 font-mono text-gray-900">
                      {t.metricKey}
                    </td>
                    <td className="px-sp-3 py-sp-2 text-right font-mono text-gray-900">
                      {t.warningMin.toFixed(2)} – {t.warningMax.toFixed(2)}
                    </td>
                    <td className="px-sp-3 py-sp-2 text-right font-mono text-gray-900">
                      {t.criticalMin.toFixed(2)} – {t.criticalMax.toFixed(2)}
                    </td>
                    <td className="px-sp-3 py-sp-2 text-gray-700">{t.unit}</td>
                    <td className="px-sp-3 py-sp-2">
                      <StatusPill
                        level={t.enabled ? 'healthy' : 'watch'}
                        label={t.enabled ? 'ON' : 'OFF'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
